import React, { useState, useEffect } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { notify } from '@/utils/notify';
import { useTranslation } from 'react-i18next';
import { readOptionalProtocolFees } from '@/utils/rewardMath';
import '@/styles/hacker-aesthetic.css';

interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: string;
  type: 'node' | 'chest' | 'upgrade';
  available: boolean;
  tier?: number;
}

interface ChestPurchaseResult {
  chestName: string;
  chestTier: number;
  chestId: string | null;
  mintBlock: number;
  timestamp: number;
}

type NodeTier = 1 | 2 | 3 | 4 | 5;

interface NodePurchaseResult {
  itemName: string;
  nodeId: string;
  tier: NodeTier;
  hashPower: string;
  bandwidth: string;
  uptime: string;
  timestamp: number;
}

interface ShopPanelProps {
  onClose: () => void;
  balance?: string;
}

const splitPrice = (price: string) => {
  const [amount, ...rest] = price.split(' ');
  return { amount, currency: rest.join(' ') || 'ETCPOWv2' };
};

const ensureAllowance = async (
  token: any,
  owner: string,
  spender: string,
  amount: bigint
) => {
  const current: bigint = await token.allowance(owner, spender);
  if (current >= amount) return;
  toast.loading('Approving token spending...', { id: 'approve' });
  const approveTx = await token.approve(spender, amount);
  await approveTx.wait();
  toast.success('Token approved', { id: 'approve' });
};

export const ShopPanel: React.FC<ShopPanelProps> = ({ onClose, balance }) => {
  const { contracts, signer, isConnected, isWrongNetwork, contractsReady } = useNetwork();
  const { t } = useTranslation();
  const [items, setItems] = useState<ShopItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'node' | 'chest'>('node');
  const [chestPurchaseResult, setChestPurchaseResult] = useState<ChestPurchaseResult | null>(null);
  const [referralCode, setReferralCode] = useState('');
  const [purchaseResult, setPurchaseResult] = useState<NodePurchaseResult | null>(null);
  const [reactivationFee, setReactivationFee] = useState<string | null>(null);
  const [protocolFeeBps, setProtocolFeeBps] = useState<number | null>(null);

  const getChestTier = (item: ShopItem) => {
    if (item.id.includes('legendary')) return 5;
    if (item.id.includes('epic')) return 4;
    if (item.id.includes('rare')) return 3;
    if (item.id.includes('uncommon')) return 2;
    return 1;
  };

  useEffect(() => {
    if (isConnected && contracts) {
      void loadShopItems();
    }
  }, [isConnected, contracts]);

  const loadShopItems = async () => {
    if (!signer || !contracts) return;

    try {
      setLoading(true);

      let tierPrices = ['10', '25', '50', '100', '250'];
      const chestTierPrices = ['2', '4', '6', '8', '10'];

      if (contracts.gameLogic) {
        try {
          const tierPriceCalls = [
            contracts.gameLogic.nodePurchasePrice(),
            contracts.gameLogic.tier2NodePrice(),
            contracts.gameLogic.tier3NodePrice(),
            contracts.gameLogic.tier4NodePrice(),
            contracts.gameLogic.tier5NodePrice(),
          ];
          const tierPricesWei = await Promise.all(tierPriceCalls);
          tierPrices = tierPricesWei.map((value: bigint) => ethers.formatEther(value));

          for (let i = 1; i <= 5; i += 1) {
            const priceWei = await contracts.gameLogic.chestTierPrices(i);
            chestTierPrices[i - 1] = ethers.formatEther(priceWei);
          }
        } catch (error) {
          console.error('Failed to fetch prices:', error);
        }

        try {
          const fees = await readOptionalProtocolFees(contracts.gameLogic);
          setReactivationFee(
            fees.reactivationFeeWei != null
              ? ethers.formatEther(fees.reactivationFeeWei)
              : null
          );
          setProtocolFeeBps(fees.protocolFeeBps);
        } catch {
          setReactivationFee(null);
          setProtocolFeeBps(null);
        }
      }

      const shopItems: ShopItem[] = [
        {
          id: 'tier1-node',
          name: 'Tier 1 Node',
          description: 'Basic mining node to start your journey',
          price: `${tierPrices[0]} ETCPOWv2`,
          type: 'node',
          available: true,
          tier: 1,
        },
        {
          id: 'tier2-node',
          name: 'Tier 2 Node',
          description: 'Advanced node with higher earning potential',
          price: `${tierPrices[1]} ETCPOWv2`,
          type: 'node',
          available: true,
          tier: 2,
        },
        {
          id: 'tier3-node',
          name: 'Tier 3 Node',
          description: 'Professional-grade mining node',
          price: `${tierPrices[2]} ETCPOWv2`,
          type: 'node',
          available: true,
          tier: 3,
        },
        {
          id: 'tier4-node',
          name: 'Tier 4 Node',
          description: 'Enterprise-level mining performance',
          price: `${tierPrices[3]} ETCPOWv2`,
          type: 'node',
          available: true,
          tier: 4,
        },
        {
          id: 'tier5-node',
          name: 'Tier 5 Node',
          description: 'Ultimate mining node with maximum potential',
          price: `${tierPrices[4]} ETCPOWv2`,
          type: 'node',
          available: true,
          tier: 5,
        },
        {
          id: 'basic-chest',
          name: t('chests.basic'),
          description: `${t('chests.basic_desc')} — Opens next block for hash/bandwidth/uptime upgrades`,
          price: `${chestTierPrices[0]} ETCPOWv2`,
          type: 'chest',
          available: true,
        },
        {
          id: 'uncommon-chest',
          name: t('chests.uncommon'),
          description: `${t('chests.uncommon_desc')} — Higher chance for rare upgrades`,
          price: `${chestTierPrices[1]} ETCPOWv2`,
          type: 'chest',
          available: true,
        },
        {
          id: 'rare-chest',
          name: t('chests.rare'),
          description: `${t('chests.rare_desc')} — Epic and legendary loot odds`,
          price: `${chestTierPrices[2]} ETCPOWv2`,
          type: 'chest',
          available: true,
        },
        {
          id: 'epic-chest',
          name: t('chests.epic'),
          description: `${t('chests.epic_desc')} — High-tier upgrade rewards`,
          price: `${chestTierPrices[3]} ETCPOWv2`,
          type: 'chest',
          available: true,
        },
        {
          id: 'legendary-chest',
          name: t('chests.legendary'),
          description: `${t('chests.legendary_desc')} — Best loot odds in the game`,
          price: `${chestTierPrices[4]} ETCPOWv2`,
          type: 'chest',
          available: true,
        },
      ];

      setItems(shopItems);
    } catch (error) {
      console.error('Failed to load shop items:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseItem = async (item: ShopItem) => {
    if (!contracts.gameLogic || !contracts.etcpowv2Token || !signer) return;
    if (isWrongNetwork || !contractsReady) {
      notify.error('Switch to the correct Ethereum Classic network first');
      return;
    }

    try {
      setLoading(true);
      try {
        const paused = await contracts.gameLogic.paused();
        if (paused) {
          notify.error('Game is paused by owner — purchases are disabled');
          return;
        }
      } catch {
        /* older ABI without paused() */
      }

      if (item.type === 'node') {
        let purchaseFunction = '';

        switch (item.tier) {
          case 1:
            purchaseFunction = 'purchaseStarterNode';
            break;
          case 2:
            purchaseFunction = 'purchaseTier2Node';
            break;
          case 3:
            purchaseFunction = 'purchaseTier3Node';
            break;
          case 4:
            purchaseFunction = 'purchaseTier4Node';
            break;
          case 5:
            purchaseFunction = 'purchaseTier5Node';
            break;
          default:
            throw new Error('Invalid node tier');
        }

        let priceWei;
        if (item.tier === 1) {
          priceWei = await contracts.gameLogic.nodePurchasePrice();
        } else if (item.tier === 2) {
          priceWei = await contracts.gameLogic.tier2NodePrice();
        } else if (item.tier === 3) {
          priceWei = await contracts.gameLogic.tier3NodePrice();
        } else if (item.tier === 4) {
          priceWei = await contracts.gameLogic.tier4NodePrice();
        } else if (item.tier === 5) {
          priceWei = await contracts.gameLogic.tier5NodePrice();
        } else {
          throw new Error('Invalid node tier');
        }

        const ownerAddress = await signer.getAddress();
        await ensureAllowance(
          contracts.etcpowv2Token,
          ownerAddress,
          contracts.gameLogic.target,
          priceWei
        );

        toast.loading(`Purchasing Tier ${item.tier || 'Unknown'} node...`, { id: 'purchase' });
        const purchaseFn = contracts.gameLogic[purchaseFunction];
        if (!purchaseFn) {
          throw new Error(`Missing purchase function: ${purchaseFunction}`);
        }

        const tx = await purchaseFn(referralCode || '');
        const receipt = await tx.wait();
        toast.success('Node purchased — Activate it in Node Control to start earning', { id: 'purchase' });

        let actualNodeId = 'NODE-???';
        const purchasedTier = Math.min(5, Math.max(1, item.tier ?? 1)) as NodeTier;
        const nodeDetails: Omit<NodePurchaseResult, 'itemName' | 'nodeId' | 'timestamp'> = {
          tier: purchasedTier,
          hashPower: `${10 * purchasedTier} HP`,
          bandwidth: `${10 * purchasedTier} BW`,
          uptime: `${10 * purchasedTier} UT`,
        };

        const nodePurchasedEvent = receipt.logs?.find((log: any) => {
          try {
            const parsed = contracts.gameLogic.interface.parseLog(log);
            return parsed?.name === 'NodePurchased';
          } catch {
            return false;
          }
        });

        if (nodePurchasedEvent && contracts.nodeNFT) {
          const parsed = contracts.gameLogic.interface.parseLog(nodePurchasedEvent);
          const nid = parsed.args.nodeId.toString();
          actualNodeId = `NODE-${nid}`;
          try {
            const stats = await contracts.nodeNFT.getNodeStats(nid);
            nodeDetails.hashPower = `${stats.hashPower.toString()} HP`;
            nodeDetails.bandwidth = `${stats.bandwidth.toString()} BW`;
            nodeDetails.uptime = `${stats.uptime.toString()} UT`;
            nodeDetails.tier = Number(stats.tier) as NodeTier;
          } catch {
            /* keep tier-scaled defaults */
          }
        }

        setPurchaseResult({
          itemName: item.name,
          nodeId: actualNodeId,
          ...nodeDetails,
          timestamp: Date.now(),
        });
      } else if (item.type === 'chest') {
        let chestTier = 1;
        if (item.id.includes('uncommon')) chestTier = 2;
        else if (item.id.includes('rare')) chestTier = 3;
        else if (item.id.includes('epic')) chestTier = 4;
        else if (item.id.includes('legendary')) chestTier = 5;

        const chestPriceWei = await contracts.gameLogic.chestTierPrices(chestTier);
        const ownerAddress = await signer.getAddress();
        await ensureAllowance(
          contracts.etcpowv2Token,
          ownerAddress,
          contracts.gameLogic.target,
          chestPriceWei
        );

        toast.loading('Purchasing chest...', { id: 'purchase' });
        const tx = await contracts.gameLogic.purchaseChest(chestTier);
        const purchaseReceipt = await tx.wait();

        let chestId: string | null = null;
        let mintBlock = Number(purchaseReceipt.blockNumber || 0);
        const chestPurchasedEvent = purchaseReceipt.logs?.find((log: any) => {
          try {
            const parsed = contracts.gameLogic.interface.parseLog(log);
            return parsed?.name === 'ChestPurchased';
          } catch {
            return false;
          }
        });

        if (chestPurchasedEvent) {
          const parsed = contracts.gameLogic.interface.parseLog(chestPurchasedEvent);
          chestId = parsed.args.chestId.toString();
        }

        if (chestId && contracts.chestNFT) {
          try {
            const details = await contracts.chestNFT.getChestDetails(chestId);
            mintBlock = Number(details.mintBlock ?? mintBlock);
          } catch {
            /* use receipt block */
          }
        }

        setChestPurchaseResult({
          chestName: item.name,
          chestTier,
          chestId,
          mintBlock,
          timestamp: Date.now(),
        });
        toast.success('Chest purchased — open from Inventory after the next block', {
          id: 'purchase',
        });
      }

      await loadShopItems();
      window.dispatchEvent(new CustomEvent('itemPurchased', { detail: { type: item.type, success: true } }));
    } catch (error: any) {
      console.error('Failed to purchase item:', error);
      toast.dismiss('approve');
      toast.dismiss('purchase');

      if (error.code === 4001) {
        notify.error(null, 'Transaction cancelled', { id: 'purchase' });
      } else {
        notify.error(error, 'Purchase failed — check balance and try again', { id: 'purchase' });
      }
    } finally {
      setLoading(false);
    }
  };

  const getItemsByType = (type: 'node' | 'chest') => items.filter((item) => item.type === type);

  return (
    <div className="shop-panel">
      <div className="shop-header">
        <h2 className="shop-title">{t('shop.title')}</h2>
        <div className="shop-stats">
          <span className="shop-micro-label">{t('shop.balance')}</span>
          <span className="shop-balance-value">{balance || t('dashboard.loading')}</span>
        </div>
        <button type="button" className="shop-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className="shop-fee-strip" aria-live="polite">
        {reactivationFee != null || protocolFeeBps != null ? (
          <>
            {reactivationFee != null && (
              <span>
                Reactivation <strong>{reactivationFee}</strong> ETCPOWv2
              </span>
            )}
            {protocolFeeBps != null && (
              <span>
                Protocol fee <strong>{(protocolFeeBps / 100).toFixed(2)}%</strong>
              </span>
            )}
          </>
        ) : (
          <span>
            Fees: reactivation / protocol cut load when contract methods are live — until then, none shown.
          </span>
        )}
      </div>

      <div className="shop-segment" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'node'}
          className={`shop-segment-btn ${activeTab === 'node' ? 'active' : ''}`}
          onClick={() => setActiveTab('node')}
        >
          {t('items.node')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'chest'}
          className={`shop-segment-btn ${activeTab === 'chest' ? 'active' : ''}`}
          onClick={() => setActiveTab('chest')}
        >
          {t('items.chest')}
        </button>
      </div>

      {activeTab === 'node' && (
        <div className="shop-referral">
          <label htmlFor="referral-code" className="shop-micro-label">
            Referral Code
          </label>
          <div className="shop-referral-row">
            <input
              id="referral-code"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="Optional"
              className="referral-input"
              maxLength={16}
            />
          </div>
          <small className="referral-help">
            Earn 1 ETCPOWv2 for the referrer when you buy with a valid code
          </small>
        </div>
      )}

      <div className="items-grid">
        {loading ? (
          <div className="loading-message">Loading shop items...</div>
        ) : (
          getItemsByType(activeTab).map((item) => {
            const tier = item.type === 'node' ? (item.tier ?? 1) : getChestTier(item);
            const { amount, currency } = splitPrice(item.price);

            return (
              <div
                key={item.id}
                className={`shop-item shop-item--${item.type} tier-${tier}`}
              >
                <div className="shop-item-top">
                  <span className={`shop-tier-badge tier-${tier}`}>T{tier}</span>
                  {item.type === 'chest' && (
                    <span className="shop-micro-label shop-loot-tag">Loot chest</span>
                  )}
                </div>

                <h3 className="item-name">{item.name}</h3>
                <p className="item-description">{item.description}</p>

                {item.type === 'chest' && (
                  <div className="chest-loot-hint">Higher tiers = better loot</div>
                )}

                <div className="item-price-block">
                  <span className="price-amount">{amount}</span>
                  <span className="price-currency">{currency}</span>
                </div>

                <button
                  type="button"
                  className={`purchase-button ${item.available ? 'available' : 'disabled'}`}
                  onClick={() => purchaseItem(item)}
                  disabled={!item.available || loading}
                >
                  {item.available ? t('shop.purchase') : t('shop.unavailable')}
                </button>
              </div>
            );
          })
        )}
      </div>

      {!isConnected && (
        <div className="connect-message">
          {t('messages.wallet_connect')}
        </div>
      )}

      {purchaseResult && (
        <div className="node-purchase-overlay">
          <div className="result-container reveal-burst">
            <div className="result-header">
              <h2 className="node-title">Node Purchased</h2>
              <button
                type="button"
                className="result-close"
                onClick={() => setPurchaseResult(null)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="node-info">
                <span className="node-name">{purchaseResult.itemName}</span>
                <span className="node-id">{purchaseResult.nodeId}</span>
              </div>
            </div>

            <div className="node-display">
              <div className={`node-card tier-${purchaseResult.tier ?? 1}`}>
                <div className="node-header">
                  <span className="node-icon">NODE</span>
                  <span className="node-name">{purchaseResult.itemName}</span>
                </div>
                <div className="node-stats">
                  <div className="stat-row">
                    <span className="stat-label">Tier</span>
                    <span className="stat-value">{purchaseResult.tier}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Hash Power</span>
                    <span className="stat-value">{purchaseResult.hashPower}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Bandwidth</span>
                    <span className="stat-value">{purchaseResult.bandwidth}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Uptime</span>
                    <span className="stat-value">{purchaseResult.uptime}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="result-actions">
              <button
                type="button"
                className="hacker-button success"
                onClick={() => {
                  setPurchaseResult(null);
                  window.dispatchEvent(new CustomEvent('itemPurchased', { detail: { type: 'node', success: true } }));
                }}
              >
                {t('actions.continue', { defaultValue: 'Continue' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {chestPurchaseResult && (
        <div className="chest-opening-overlay">
          <div className="result-container reveal-flip">
            <div className="result-header">
              <h2 className="chest-title">Chest Purchased</h2>
              <button
                type="button"
                className="result-close"
                onClick={() => setChestPurchaseResult(null)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="chest-info">
                <span className="chest-name">{chestPurchaseResult.chestName}</span>
                <span className="chest-tier">
                  {t('nodes.tier')} {chestPurchaseResult.chestTier}
                </span>
              </div>
            </div>

            <div className="node-display">
              <div className={`node-card tier-${chestPurchaseResult.chestTier}`}>
                <div className="node-header">
                  <span className="node-icon">CHEST</span>
                  <span className="node-name">
                    {chestPurchaseResult.chestId
                      ? `CHEST-${chestPurchaseResult.chestId}`
                      : chestPurchaseResult.chestName}
                  </span>
                </div>
                <div className="node-stats">
                  <div className="stat-row">
                    <span className="stat-label">Status</span>
                    <span className="stat-value">Sealed</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Mint block</span>
                    <span className="stat-value">{chestPurchaseResult.mintBlock || '—'}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Open after</span>
                    <span className="stat-value">Next block</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="shop-chest-hint">
              Chests open one block after purchase to keep loot fair. Open from Inventory when ready.
            </p>

            <div className="result-actions">
              <button
                type="button"
                className="hacker-button success"
                onClick={() => {
                  setChestPurchaseResult(null);
                  onClose();
                  window.dispatchEvent(new CustomEvent('openInventory'));
                }}
              >
                Open Inventory
              </button>
              <button type="button" className="hacker-button" onClick={() => setChestPurchaseResult(null)}>
                {t('actions.continue', { defaultValue: 'Continue' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
